/** Lester — the friendly floating site chatbot. */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sessionId() {
  let sid = localStorage.getItem('lester_session');
  if (!sid) {
    sid = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('lester_session', sid);
  }
  return sid;
}

const GREETING = {
  role: 'assistant',
  text: "Hello! I'm Lester — ask me anything about days out, events or places to stay in Leicestershire.",
};

export default function LesterChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/lester/chat`, { session_id: sessionId(), message: text });
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply, cards: r.data.cards || [], external: r.data.external, sources: r.data.sources || [] }]);
    } catch (e) {
      const msg = e?.response?.status === 429
        ? "We've had a good long natter! Refresh the page to start a fresh chat."
        : "Sorry, I've had a moment — try me again in a second.";
      setMessages((m) => [...m, { role: 'assistant', text: msg }]);
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="lester-open-btn" aria-label="Chat with Lester"
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-heritage text-cream rounded-full pl-4 pr-5 py-3 shadow-lg hover:scale-105 transition-transform">
          <MessageCircle size={20} />
          <span className="text-sm font-semibold">Ask Lester</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[min(92vw,380px)] bg-base rounded-3xl shadow-2xl border border-charcoal/15 flex flex-col overflow-hidden" data-testid="lester-panel" style={{ height: 'min(70vh, 560px)' }}>
          <div className="bg-heritage text-cream px-5 py-4 flex items-center justify-between">
            <div>
              <div className="font-display text-lg leading-tight">Lester</div>
              <div className="text-[11px] opacity-80">Your friendly Leicestershire local</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="lester-close-btn" aria-label="Close chat" className="p-1.5 hover:bg-white/15 rounded-lg"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="lester-messages">
            {messages.map((m, i) => (
              <div key={i}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'ml-auto bg-forest text-base rounded-br-md' : 'bg-white border border-charcoal/10 rounded-bl-md'}`}
                  data-testid={`lester-msg-${m.role}`}>
                  {m.text}
                </div>
                {m.external && (
                  <div className="max-w-[85%] mt-1.5 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="lester-external-note">
                    <div className="text-[11px] font-semibold text-amber-800">Sourced from the web — not yet a GoLeicestershire listing</div>
                    {(m.sources || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {m.sources.map((s) => (
                          <a key={s} href={s} target="_blank" rel="noopener noreferrer" data-testid="lester-external-source"
                            className="text-[11px] text-amber-700 underline truncate max-w-[220px]">
                            {(() => { try { return new URL(s).hostname.replace('www.', ''); } catch { return s; } })()}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {(m.cards || []).length > 0 && (
                  <div className="mt-2 space-y-2" data-testid="lester-cards">
                    {m.cards.map((c) => (
                      <a key={c.href + c.name} href={c.href} data-testid="lester-card"
                        className="flex items-center gap-3 max-w-[85%] bg-white border border-charcoal/10 rounded-2xl p-2 pr-3 hover:border-heritage hover:shadow-sm transition-all">
                        {c.image
                          ? <img src={c.image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${c.image}` : c.image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" />
                          : <div className="w-14 h-14 rounded-xl bg-heritage/10 shrink-0 flex items-center justify-center"><MessageCircle size={18} className="text-heritage/50" /></div>}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{c.name}</div>
                          <div className="text-[11px] text-charcoal/55 truncate">
                            {c.kind === 'event' ? `Event${c.date ? ` · ${c.date.split('-').reverse().join('/')}` : ''}` : c.kind === 'page' ? (c.label || 'GoLeicestershire') : c.town || 'Leicestershire'}
                          </div>
                        </div>
                        <span className="text-heritage text-sm shrink-0">→</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="bg-white border border-charcoal/10 rounded-2xl rounded-bl-md px-4 py-3 w-16 flex gap-1" data-testid="lester-typing">
                {[0, 1, 2].map((d) => (
                  <span key={d} className="w-1.5 h-1.5 bg-charcoal/40 rounded-full animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="border-t border-charcoal/10 bg-white p-3 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask about days out, events…" maxLength={600}
              data-testid="lester-input"
              className="flex-1 bg-base border border-charcoal/15 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-heritage" />
            <button onClick={send} disabled={busy || !input.trim()} data-testid="lester-send-btn" aria-label="Send"
              className="bg-heritage text-cream rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-40">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
